import { useEffect, useState, useRef, createContext, useContext, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { toast } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { api } from "./lib/api";
import { DEFAULT_OPTIONS } from "./lib/constants";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import "./i18n";
import { LanguageProvider } from "./contexts/LanguageContext";
import { CurrencyIdentityProvider } from "./contexts/CurrencyIdentityContext";
import { BuilderCommandPaletteProvider } from "./components/builders/BuilderCommandPalette";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import TrustCenter from "./pages/Trust/TrustCenter";
import TrustBundleVerify from "./pages/Trust/TrustBundleVerify";
import CreditDepletionModal from "./components/CreditDepletionModal";
// Sprint 921 — go-live hostname gate (serves ComingSoon on apex domain).
import { shouldGateToComingSoon, processDemoUnlock, refreshLaunchModeFromAPI } from "./lib/hostnameGate";
// — Lazy-loaded heavy pages (Sprint 3J-h perf pass) — cuts ~70% off the
//   initial bundle. All of these pages are navigated-to, not first-paint.
const LeadsPage = lazy(() => import("./pages/LeadsPage"));
const CreateLeadPage = lazy(() => import("./pages/CreateLeadPage"));
const LeadDetailPage = lazy(() => import("./pages/LeadDetailPage"));
const KanbanPage = lazy(() => import("./pages/KanbanPage"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const EmailIngestPage = lazy(() => import("./pages/EmailIngestPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const AccessCenterPage = lazy(() => import("./pages/AccessCenterPage"));
const QuotesPage = lazy(() => import("./pages/QuotesPage"));
const QuoteDetailPage = lazy(() => import("./pages/QuoteDetailPage"));
const CatalogPage = lazy(() => import("./pages/CatalogPage"));
const CatalogItemPage = lazy(() => import("./pages/CatalogItemPage"));
const KitsPage = lazy(() => import("./pages/KitsPage"));
const BrandingPage = lazy(() => import("./pages/BrandingPage"));
const DuplicatesPage = lazy(() => import("./pages/DuplicatesPage"));
const QuoteComparePage = lazy(() => import("./pages/QuoteComparePage"));
const QuoteBuilderPage = lazy(() => import("./pages/QuoteBuilderPage"));
const ProposalComposerPage = lazy(() => import("./pages/ProposalComposerPage"));
const BriefsPage = lazy(() => import("./pages/BriefsPage"));
const BriefEditorPage = lazy(() => import("./pages/BriefEditorPage"));
const BriefPublicPage = lazy(() => import("./pages/BriefPublicPage"));
const ClientsPage = lazy(() => import("./pages/ClientsPage"));
const InvoicesPage = lazy(() => import("./pages/InvoicesPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const BundlesPage = lazy(() => import("./pages/BundlesPage"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const ProjectDetailPage = lazy(() => import("./pages/ProjectDetailPage"));
const ProductionCalendarPage = lazy(() => import("./pages/ProductionCalendarPage"));
const MyTasksPage = lazy(() => import("./pages/MyTasksPage"));
const GanttPage = lazy(() => import("./pages/GanttPage"));
const CrewResourcesPage = lazy(() => import("./pages/CrewResourcesPage"));
const ResourcesPage = lazy(() => import("./pages/ResourcesPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const PublicTermsPage = lazy(() => import("./pages/PublicTermsPage"));
const ImprintPage = lazy(() => import("./pages/ImprintPage"));
const GDPRPage = lazy(() => import("./pages/GDPRPage"));
const ClientPortalPage = lazy(() => import("./pages/ClientPortalPage"));
const PublicQuotePage = lazy(() => import("./pages/PublicQuotePage"));
const AdministrationPage = lazy(() => import("./pages/AdministrationPage"));
const FinancialsPage = lazy(() => import("./pages/FinancialsPage"));
const MarketingDashboardPage = lazy(() => import("./pages/MarketingDashboardPage"));
const MailingSystemPage = lazy(() => import("./pages/MailingSystemPage"));
const EmailMarketplacePage = lazy(() => import("./pages/EmailMarketplacePage"));
const ContractsPage = lazy(() => import("./pages/ContractsPage"));
const ContractSignPage = lazy(() => import("./pages/ContractSignPage"));
const CatalogHealthPage = lazy(() => import("./pages/CatalogHealthPage"));
const MaintenanceCalendarPage = lazy(() => import("./pages/MaintenanceCalendarPage"));
const GearCalendarPage = lazy(() => import("./pages/GearCalendarPage"));
const CrewDirectoryPage = lazy(() => import("./pages/CrewDirectoryPage"));
import SkillMatrixPage from "./pages/SkillMatrixPage";
const CrewMemberPage = lazy(() => import("./pages/CrewMemberPage"));
const CrewPortalPage = lazy(() => import("./pages/CrewPortalPage"));
const SupplierPortalPage = lazy(() => import("./pages/SupplierPortalPage"));
const PublicGalleryPage = lazy(() => import("./pages/PublicGalleryPage"));
const PackageMicrositePage = lazy(() => import("./pages/PackageMicrositePage"));
const RateCardsPage = lazy(() => import("./pages/RateCardsPage"));
const RateIntelligencePage = lazy(() => import("./pages/RateIntelligencePage"));
const ProjectCalendarPage = lazy(() => import("./pages/ProjectCalendarPage"));
const CalendarSubscriptionsPage = lazy(() => import("./pages/CalendarSubscriptionsPage"));
const ProfitabilityPage = lazy(() => import("./pages/ProfitabilityPage"));
const ReportingPage = lazy(() => import("./pages/ReportingPage"));
const ClientHealthPage = lazy(() => import("./pages/ClientHealthPage"));
const WebsiteBuilderPage = lazy(() => import("./pages/WebsiteBuilderPage"));
// Sprint 1060a — Web Studio unifying shell.
const WebStudioPage = lazy(() => import("./pages/WebStudioPage"));
// Sprint 1069.D — Sales Studio pilot shell.
const SalesStudioPage = lazy(() => import("./pages/SalesStudioPage"));
const MarketingStudioPage = lazy(() => import("./pages/MarketingStudioPage"));
const ProjectsStudioPage = lazy(() => import("./pages/ProjectsStudioPage"));
const CatalogStudioPage = lazy(() => import("./pages/CatalogStudioPage"));
// Sprint 1070.A0 — Industry-adaptive Finance + Operate Studios
const FinanceStudioPage = lazy(() => import("./pages/FinanceStudioPage"));
const OperateStudioPage = lazy(() => import("./pages/OperateStudioPage"));
// Sprint 1065 — Concept Room (SALES → Project Development chat-based ideation).
const ConceptRoomListPage = lazy(() => import("./pages/ConceptRoomListPage"));
const ConceptRoomSessionPage = lazy(() => import("./pages/ConceptRoomSessionPage"));
const WebsiteEditorPage = lazy(() => import("./pages/WebsiteEditorPage"));
const TemplateGalleryPage = lazy(() => import("./pages/TemplateGalleryPage"));
const IndustryTemplateGalleryPage = lazy(() => import("./pages/IndustryTemplateGalleryPage"));
const CheckoutResultPage = lazy(() => import("./pages/CheckoutResultPage"));
const MarketResearcherPage = lazy(() => import("./pages/MarketResearcherPage"));
const BrandSetupPage = lazy(() => import("./pages/BrandSetupPage"));
const BrandDiagnosticPage = lazy(() => import("./pages/BrandDiagnosticPage"));
// Sprint 1069.E1-v2-Agent2 — Magic Chain · Agent 2 · Positioning.
// (PositioningAgentPage import removed — /marketing/positioning now redirects to Brand Studio.)
// Sprint 1069.E1-v2-Agent3 — Magic Chain · Agent 3 · 30-Day Plan.
const PlanAgentPage = lazy(() => import("./pages/PlanAgentPage"));
// Sprint 1069.E1-v2-Agent4 — Magic Chain · Agent 4 · Social Batch.
const SocialBatchAgentPage = lazy(() => import("./pages/SocialBatchAgentPage"));
// Sprint 1069.E1-v2-Agent5 — Magic Chain · Agent 5 · Email Sequence.
const EmailSequenceAgentPage = lazy(() => import("./pages/EmailSequenceAgentPage"));
// Sprint 1069.E1-v2-Agent6 — Magic Chain · Agent 6 · Landing Page Draft.
const LandingPageAgentPage = lazy(() => import("./pages/LandingPageAgentPage"));
const BrandDashboardPage = lazy(() => import("./pages/BrandDashboardPage"));
const BrandLaunchPage = lazy(() => import("./pages/BrandLaunchPage"));
const MarketingPlanPage = lazy(() => import("./pages/MarketingPlanPage"));
const MarketingROIPage = lazy(() => import("./pages/MarketingROIPage"));
const BrandRepositoryPage = lazy(() => import("./pages/BrandRepositoryPage"));
const SEOAnalyzerPage = lazy(() => import("./pages/SEOAnalyzerPage"));
const LegacySocialHubRedirect = lazy(() => import("./pages/LegacySocialHubRedirect"));
const CreativeHubPage = lazy(() => import("./pages/CreativeHubPage"));
const CompetitorIntelPage = lazy(() => import("./pages/CompetitorIntelPage"));
const MissionControlPage = lazy(() => import("./pages/MissionControlPage"));
const StandaloneDeliverablesPage = lazy(() => import("./pages/StandaloneDeliverablesPage"));
const CompanyBrandingPage = lazy(() => import("./pages/CompanyBrandingPage"));
// Sprint Brand-Studio-Surface-Restore (2026-06-05) — wire the 17 /brand-studio/*
// routes that were defined in missionRegistry + BrandStudioPage SECTION_REGISTRY
// but never wired in App.js. Per /app/memory/SYSTEM_AUDIT/brand-studio-canonical-architecture.md.
const BrandStudioPage = lazy(() => import("./pages/BrandStudioPage"));
const BrandStudioHomePage = lazy(() => import("./pages/BrandStudioHomePage"));
const BrandVoicePage = lazy(() => import("./pages/BrandVoicePage"));
const BrandIdentityAnalysisPage = lazy(() => import("./pages/BrandStudio/BrandIdentityAnalysisPage"));
const BrandIdentityReportPage = lazy(() => import("./pages/BrandStudio/BrandIdentityReportPage"));
const BrandPerformancePage = lazy(() => import("./pages/BrandStudio/BrandPerformancePage"));
const BrandProtectionWorkspacePage = lazy(() => import("./pages/BrandStudio/BrandProtectionWorkspacePage"));
const DomainPortfolioPage = lazy(() => import("./pages/BrandStudio/DomainPortfolioPage"));
const EvidenceCenterPage = lazy(() => import("./pages/BrandStudio/EvidenceCenterPage"));
const LivingBIPage = lazy(() => import("./pages/BrandStudio/LivingBIPage"));
const PitchBriefPage = lazy(() => import("./pages/PitchBriefPage"));
const IntakeFormsPage = lazy(() => import("./pages/IntakeFormsPage"));
const ChatConversationsPage = lazy(() => import("./pages/ChatConversationsPage"));
const ProposalBuilderListPage = lazy(() => import("./pages/ProposalBuilderListPage"));
const ImportDraftDetailPage = lazy(() => import("./pages/ImportDraftPages"));
const ImportDraftsListPage = lazy(() =>
  import("./pages/ImportDraftPages").then((m) => ({ default: m.ImportDraftsListPage }))
);
const EducationControlRoomPage = lazy(() => import("./pages/EducationControlRoomPage"));
const ClassroomInvoicingPage = lazy(() => import("./pages/Platform/ClassroomInvoicingPage"));
const R2BrowserPage = lazy(() => import("./pages/Platform/R2BrowserPage"));
const ForSchoolsPage = lazy(() => import("./pages/StudentLandingPages"));
const ForStudentsPage = lazy(() =>
  import("./pages/StudentLandingPages").then((m) => ({ default: m.ForStudentsPage }))
);
const EducatorPublicPage = lazy(() => import("./pages/EducatorPublicPage"));
const IndustryAwardsPage = lazy(() => import("./pages/IndustryAwardsPage"));
const BlogAdminPage = lazy(() => import("./pages/BlogAdminPage"));
const ClinicPublicPage = lazy(() => import("./pages/ClinicPublicPage"));
const ExternalSiteIntegratePage = lazy(() => import("./pages/Platform/ExternalSiteIntegratePage"));
const ExternalSiteHubPage = lazy(() => import("./pages/Platform/ExternalSiteHubPage"));
const MigrationWizardPage = lazy(() => import("./pages/Platform/MigrationWizardPage"));
const CatalogWidgetsPage = lazy(() => import("./pages/Platform/CatalogWidgetsPage"));
const NewsletterWidgetsPage = lazy(() => import("./pages/Platform/NewsletterWidgetsPage"));
const ShopWidgetsPage = lazy(() => import("./pages/Platform/ShopWidgetsPage"));
const ApiKeysPage = lazy(() => import("./pages/Platform/ApiKeysPage"));
const PayoutsPage = lazy(() => import("./pages/Platform/PayoutsPage"));
const MarketplaceRevenuePage = lazy(() => import("./pages/Platform/MarketplaceRevenuePage"));
// Sprint 982 — Pre-Launch Readiness dashboard
const ReadinessPage = lazy(() => import("./pages/Platform/ReadinessPage"));
const PlatformIntegrationsPage = lazy(() => import("./pages/Platform/IntegrationsPage"));
const ConversionFunnelPage = lazy(() => import("./pages/Platform/ConversionFunnelPage"));
// Sprint 1070.C5-prep5 — Web Studio shell wrap for external-site canonical pages.
import WebStudioShellWrap from "./components/loop/WebStudioShellWrap";
const ProposalBuilderEditorPage = lazy(() => import("./pages/ProposalBuilderEditorPage"));
const PitchEditorPage = lazy(() => import("./pages/PitchEditorPage"));
const PitchPresentPage = lazy(() => import("./pages/PitchPresentPage"));
const PitchThemeBuilderPage = lazy(() => import("./pages/PitchThemeBuilderPage"));
const PresenterViewPage = lazy(() => import("./pages/PresenterViewPage"));
const PublicPitchPage = lazy(() => import("./pages/PublicPitchPage"));
const PublicSharedPresetPage = lazy(() => import("./pages/PublicSharedPresetPage"));
const PitchRemotePage = lazy(() => import("./pages/PitchRemotePage"));
const SellerProfilePage = lazy(() => import("./pages/SellerProfilePage"));
const PackagesPage = lazy(() => import("./pages/PackagesPage"));
const ProposalAnalyticsPage = lazy(() => import("./pages/ProposalAnalyticsPage"));
const QuoteAnalyticsPage = lazy(() => import("./pages/QuoteAnalyticsPage"));
const UnifiedAnalyticsPage = lazy(() => import("./pages/UnifiedAnalyticsPage"));
const PublicProposalPage = lazy(() => import("./pages/PublicProposalPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const OnePagePitchesPage = lazy(() => import("./pages/OnePagePitchesPage"));
const ComingSoonPage = lazy(() => import("./pages/ComingSoonPage"));
const GateStatusPage = lazy(() => import("./pages/GateStatusPage"));
const WaitlistVerifiedPage = lazy(() => import("./pages/WaitlistVerifiedPage"));
const LaunchChampionsPage = lazy(() => import("./pages/LaunchChampionsPage"));
const TrustPage = lazy(() => import("./pages/TrustPage"));
const StatusPage = lazy(() => import("./pages/StatusPage"));
const KickoffsPage = lazy(() => import("./pages/KickoffsPage"));
const TemplateMarketplacePage = lazy(() => import("./pages/TemplateMarketplacePage"));
const MarketplaceHubPage = lazy(() => import("./pages/MarketplaceHubPage"));
const MarketplaceListingDetailPage = lazy(() => import("./pages/MarketplaceListingDetailPage"));
const MarketplacePurchaseSuccessPage = lazy(() => import("./pages/MarketplacePurchaseSuccessPage"));
const CreativeStudioMockPage = lazy(() => import("./pages/CreativeStudioMockPage"));
const CreativeStudioDiffPage = lazy(() => import("./pages/CreativeStudioDiffPage"));
const CreativeStudioPage = lazy(() => import("./pages/CreativeStudioPage"));
const SellerDashboardPage = lazy(() => import("./pages/SellerDashboardPage"));
const SellerPayoutsPage = lazy(() => import("./pages/SellerPayoutsPage"));
const MarketplaceAdminPage = lazy(() => import("./pages/MarketplaceAdminPage"));
const KnowledgeBasePage = lazy(() => import("./pages/KnowledgeBasePage"));
const AmbassadorPage = lazy(() => import("./pages/AmbassadorPage"));
const AmbassadorPublicPage = lazy(() => import("./pages/AmbassadorPublicPage"));
const JoinAgencyPage = lazy(() => import("./pages/JoinAgencyPage"));
const AgencyDashboardPage = lazy(() => import("./pages/AgencyDashboardPage"));
const SharedBrandKitPage = lazy(() => import("./pages/SharedBrandKitPage"));
const FlowsPage = lazy(() => import("./pages/FlowsPage"));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const ComparePage = lazy(() => import("./pages/ComparePage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const PublicBlogPostPage = lazy(() => import("./pages/PublicBlogPostPage"));
const PublicBlogIndexPage = lazy(() => import("./pages/PublicBlogIndexPage"));
const IntegrationsPage = lazy(() => import("./pages/IntegrationsPage"));
const JourneyMapPage = lazy(() => import("./pages/JourneyMapPage"));
const MeetingsPage = lazy(() => import("./pages/MeetingsPage"));
const PublicBookingPage = lazy(() => import("./pages/PublicBookingPage"));
const PublicPackagesGalleryPage = lazy(() => import("./pages/PublicPackagesGalleryPage"));
const AIPositioningPage = lazy(() => import("./pages/AIPositioningPage"));
const SuperAdminPage = lazy(() => import("./pages/SuperAdminPage"));
const SystemAuditPage = lazy(() => import("./pages/Platform/SystemAuditPage"));
const DevLibraryPage = lazy(() => import("./pages/Platform/DevLibraryPage"));
const MasterIndexPage = lazy(() => import("./pages/Platform/MasterIndexPage"));
const PCIPage = lazy(() => import("./pages/Platform/PCIPage"));
const LinksRegistryPage = lazy(() => import("./pages/Platform/LinksRegistryPage"));
const SystemFlowsPage = lazy(() => import("./pages/Platform/SystemFlowsPage"));
const ComponentMapPage = lazy(() => import("./pages/Platform/ComponentMapPage"));
const EmailLogsPage = lazy(() => import("./pages/Platform/EmailLogsPage"));
const TranslationMemoryPage = lazy(() => import("./pages/Platform/TranslationMemoryPage"));
const I18nCoveragePage = lazy(() => import("./pages/Platform/I18nCoveragePage"));
const LocalizationHubPage = lazy(() => import("./pages/Platform/LocalizationHubPage"));
const MonetizationHubPage = lazy(() => import("./pages/Platform/MonetizationHubPage"));
const OpsDashboardPage = lazy(() => import("./pages/Platform/OpsDashboardPage"));
const IntegrationsRegistryPage = lazy(() => import("./pages/Platform/IntegrationsRegistryPage"));
const WaitlistDiagPage = lazy(() => import("./pages/Platform/WaitlistDiagPage"));
const LaunchBroadcastPage = lazy(() => import("./pages/Platform/LaunchBroadcastPage"));
const NurtureDripPage = lazy(() => import("./pages/Platform/NurtureDripPage"));
const EmailDomainPage = lazy(() => import("./pages/Platform/EmailDomainPage"));
const AdminIndexPage = lazy(() => import("./pages/Platform/AdminIndexPage"));
const PermissionsPage = lazy(() => import("./pages/Platform/PermissionsPage"));
const AiUsagePage = lazy(() => import("./pages/Platform/AiUsagePage"));
const StorageHubPage = lazy(() => import("./pages/Platform/StorageHubPage"));
const AICreditsHubPage = lazy(() => import("./pages/Platform/AICreditsHubPage"));
const SecurityPostureHubPage = lazy(() => import("./pages/Platform/SecurityPostureHubPage"));
const SupportTicketsPage = lazy(() => import("./pages/Platform/SupportTicketsPage"));
const ControlRoomPage = lazy(() => import("./pages/Platform/ControlRoomPage"));
const LiveResidualDashboardPage = lazy(() => import("./pages/Platform/LiveResidualDashboardPage"));
const LaunchHubPage = lazy(() => import("./pages/Platform/LaunchHubPage"));
const GrowthDashboardPage = lazy(() => import("./pages/Platform/launch/GrowthDashboardPage"));
const LaunchBigScreenPage = lazy(() => import("./pages/Platform/launch/LaunchBigScreenPage"));
const AdminServiceTokensPage = lazy(() => import("./pages/Platform/AdminServiceTokensPage"));
// Sprint 1070.A9 — 1099 tax-season admin export
const Admin1099Page = lazy(() => import("./pages/Platform/Admin1099Page"));
// Sprint 1070.B3 — Quarter Recap (super-admin, LLM verdict)
const QuarterRecapPage = lazy(() => import("./pages/Platform/QuarterRecapPage"));
// Sprint A.1 — Brand Asset Activity Timeline (Control Room, super-admin)
const BrandAssetActivityPage = lazy(() => import("./pages/Platform/BrandAssetActivityPage"));
const FormsEnginePage = lazy(() => import("./pages/Platform/engines/FormsEnginePage"));
// Compass · G.2 (2026-05-26) — Side-by-side snapshot diff viewer
const CompassSnapshotDiffPage = lazy(() => import("./pages/Platform/CompassSnapshotDiffPage"));
// Compass · S.2 (2026-05-26) — Translation triage console
const CompassTranslationsPage = lazy(() => import("./pages/Platform/CompassTranslationsPage"));
// Compass · G.4 (2026-05-27) — Grounding Playground (hallucination-proof Q&A)
const CompassGroundingPlaygroundPage = lazy(() => import("./pages/Platform/CompassGroundingPlaygroundPage"));
// Compass · Hub Mockup (2026-05-29) — Visual preview of unified 3-tab shell
const CompassHubPage = lazy(() => import("./pages/Platform/CompassHubPage"));
// Mission Video Engine V3.1 — Human-in-the-loop marketing-video pipeline (super-admin)
const MissionVideoStudioPage = lazy(() => import("./pages/Platform/MissionVideoStudioPage"));
const QRGeneratorPage = lazy(() => import("./pages/Platform/launch/QRGeneratorPage"));
const PressKitPage = lazy(() => import("./pages/Platform/launch/PressKitPage"));
const ChangelogEditorPage = lazy(() => import("./pages/Platform/launch/ChangelogEditorPage"));
const ReferralLeaderboardPage = lazy(() => import("./pages/Platform/launch/ReferralLeaderboardPage"));
const LaunchConfigPage = lazy(() => import("./pages/Platform/launch/LaunchConfigPage"));
const PublicLeaderboardPage = lazy(() => import("./pages/PublicLeaderboardPage"));
const PublicChangelogPage = lazy(() => import("./pages/PublicChangelogPage"));
const PublicPressKitPage = lazy(() => import("./pages/PublicPressKitPage"));
const OnboardingAnalyticsFullPage = lazy(() => import("./pages/Platform/OnboardingAnalyticsFullPage"));
const SystemIntelligencePage = lazy(() => import("./pages/Platform/SystemIntelligencePage"));
const FinancialCockpitPage = lazy(() => import("./pages/Platform/FinancialCockpitPage"));
const TemplateFunnelPage = lazy(() => import("./pages/Platform/TemplateFunnelPage"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const ForSlugRouter = lazy(() => import("./pages/ForSlugRouter"));// Sprint 1089-LP v4 Phase C
const SandboxPage = lazy(() => import("./pages/SandboxPage"));
const OnboardingWizardPage = lazy(() => import("./pages/OnboardingWizardPage"));
const SSOCompletePage = lazy(() => import("./pages/SSOCompletePage"));
const PublicIntakeForm = lazy(() => import("./pages/PublicIntakeForm"));

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);
export const useOptions = () => DEFAULT_OPTIONS;
export { useTheme };

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace("#", ""));
    const sessionId = params.get("session_id");

    if (!sessionId) {
      navigate("/login");
      return;
    }

    // Exchange session_id for session_token via backend
    const promoCode = localStorage.getItem("promo_code") || "";
    const authJourney = localStorage.getItem("auth_journey") || "";
    const invitationToken = localStorage.getItem("auth_invitation_token") || "";
    api.post("/auth/session", { session_id: sessionId, promo_code: promoCode, journey: authJourney, invitation_token: invitationToken })
      .then((res) => {
        if (promoCode) localStorage.removeItem("promo_code");
        localStorage.removeItem("auth_journey");
        localStorage.removeItem("auth_invitation_token");
        // Clear hash and redirect to dashboard with user data
        window.history.replaceState(null, "", "/dashboard");
        navigate("/dashboard", { state: { user: res.data }, replace: true });
      })
      .catch((err) => {
        console.error("Auth session exchange failed:", err);
        setError(err?.response?.data?.detail || err?.message || "Authentication failed");
      });
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#19191D] flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button onClick={() => navigate("/login")} className="text-blue-400 underline">Try again</button>
        </div>
      </div>
    );
  }

  return <div className="min-h-screen bg-[#19191D] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#0A84FF] border-t-transparent rounded-full animate-spin" /></div>;
};

const ProtectedRoute = ({ children }) => {
  const [isAuth, setIsAuth] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      const { message, plan, upgrade_name, upgrade_price } = e.detail || {};
      const { toast } = require("sonner");
      const desc = upgrade_name ? `Upgrade to ${upgrade_name} (${upgrade_price ? `$${upgrade_price}/mo` : "contact us"}) to continue.` :
        plan !== "enterprise" ? "Go to Settings > Billing to upgrade your plan." : undefined;
      toast.error(message || "Plan limit reached", {
        description: desc,
        action: plan !== "enterprise" ? { label: "Upgrade", onClick: () => navigate("/settings") } : undefined,
        duration: 6000,
      });
    };
    window.addEventListener("plan-limit-reached", handler);
    return () => window.removeEventListener("plan-limit-reached", handler);
  }, [navigate]);

  useEffect(() => {
    // If user data passed from AuthCallback, skip auth check
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuth(true);
      return;
    }
    (async () => {
      try {
        const r = await api.get("/auth/me");
        setUser(r.data);
        setIsAuth(true);
      } catch {
        setIsAuth(false);
      }
    })();
  }, [location.pathname, location.state]);

  if (isAuth === null) return <div className="min-h-screen bg-[#19191D] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#0A84FF] border-t-transparent rounded-full animate-spin" /></div>;
  if (isAuth === false) return <LoginPage />;

  return <AuthContext.Provider value={{ user, setUser }}>{children}</AuthContext.Provider>;
};

const OnboardingGate = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  useEffect(() => {
    if (location.pathname === "/onboarding") { setChecked(true); return; }
    let cancelled = false;
    api.get("/onboarding/status").then(r => {
      if (cancelled) return;
      if (!r.data.onboarding_complete) {
        // Check for pending tier from landing page signup
        const pendingTier = localStorage.getItem("pending_tier");
        if (pendingTier && pendingTier !== "starter" && pendingTier !== "free") {
          // Paid tier selected — create Stripe checkout
          api.post("/monetization/subscribe", {
            tier: pendingTier,
            billing: "monthly",
            origin_url: window.location.origin,
          }).then(res => {
            localStorage.removeItem("pending_tier");
            if (res.data?.url) {
              window.location.href = res.data.url; // Redirect to Stripe
            } else {
              navigate("/onboarding", { replace: true });
            }
          }).catch(() => {
            localStorage.removeItem("pending_tier");
            navigate("/onboarding", { replace: true });
          });
        } else {
          localStorage.removeItem("pending_tier");
          setNeedsOnboarding(true);
          navigate("/onboarding", { replace: true });
        }
      } else {
        // Check for subscription activation (returning from Stripe)
        const params = new URLSearchParams(location.search);
        const sessionId = params.get("session_id");
        const subStatus = params.get("subscription");
        if (subStatus === "success" && sessionId) {
          api.post("/monetization/subscribe/activate", { session_id: sessionId })
            .then(() => {
              const { toast } = require("sonner");
              toast.success("Subscription activated! Welcome aboard.");
              navigate(location.pathname, { replace: true });
            }).catch(() => {});
        }
      }
      setChecked(true);
    }).catch(() => { if (!cancelled) setChecked(true); });
    return () => { cancelled = true; };
  }, [location.pathname, navigate]);

  if (!checked && location.pathname !== "/onboarding") {
    return <div className="min-h-screen bg-[#19191D] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#0A84FF] border-t-transparent rounded-full animate-spin" /></div>;
  }
  return children;
};

function AppRouter() {
  const location = useLocation();

  // Sprint 946I — Refresh launch_mode from public config once on mount.
  // Cached in localStorage with a 5-min TTL.  When the founder flips
  // launch_mode under /platform/launch/config the apex gate disappears
  // within ~5 minutes for users sitting on the public site, with no
  // redeploy needed.
  useEffect(() => {
    refreshLaunchModeFromAPI(process.env.REACT_APP_BACKEND_URL || "");
  }, []);

  // Check URL fragment for session_id synchronously during render (prevents race conditions)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  // Sprint 921 — Hostname gate. When the visitor is on the production apex
  // (studioplaz.com / www.studioplaz.com) AND has not unlocked via demo
  // cookie, every URL renders ComingSoonPage. The full app stays reachable
  // on app.studioplaz.com and on the Emergent preview URL.
  // Demo bypass: ?demo=<REACT_APP_DEMO_UNLOCK_TOKEN> sets a 90-day cookie.
  if (typeof window !== "undefined") {
    const cleaned = processDemoUnlock(location.search);
    if (cleaned !== null && cleaned !== location.search) {
      // Strip the demo param from the URL bar without a full reload.
      window.history.replaceState({}, "", location.pathname + cleaned + location.hash);
    }
  }
  // Sprint 946G — Gate-status diagnostic must short-circuit before the gate
  // check so it always renders on the production apex, even with no cookie.
  if (typeof window !== "undefined" && window.location.pathname === "/__gate-status") {
    return (
      <Suspense fallback={<div className="min-h-screen bg-zinc-950" />}>
        <GateStatusPage />
      </Suspense>
    );
  }
  if (shouldGateToComingSoon()) {
    // Sprint 944 — Variant pre-router under the production gate.
    // Path /business → business landing.  Otherwise UTM-aware default.
    const path = (typeof window !== "undefined" ? window.location.pathname : "/") || "/";
    const search = (typeof window !== "undefined" ? window.location.search : "") || "";
    // Lazy import to avoid bloating the initial chunk.
    // eslint-disable-next-line global-require
    const { pickLandingVariant } = require("./lib/landingVariant");
    const variant = pickLandingVariant(path, search);
    return (
      <Suspense fallback={<div className="min-h-screen bg-zinc-950" />}>
        <ComingSoonPage variant={variant} />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] text-zinc-400 text-sm">Loading…</div>}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/brief/:token" element={<BriefPublicPage />} />
      <Route path="/portal/brief/:token" element={<PitchBriefPage />} />
      <Route path="/contract/sign/:token" element={<ContractSignPage />} />
      <Route path="/portal/:token" element={<ClientPortalPage />} />
      <Route path="/client-portal/:token" element={<ClientPortalPage />} />
      <Route path="/q/:shareToken" element={<PublicQuotePage />} />
      <Route path="/deliverables/:dtoken" element={<StandaloneDeliverablesPage />} />
      <Route path="/crew-portal/:token" element={<CrewPortalPage />} />
      <Route path="/supplier-portal/:token" element={<SupplierPortalPage />} />
      <Route path="/gallery/:shareToken" element={<PublicGalleryPage />} />
      <Route path="/form/:token" element={<PublicIntakeForm />} />
      <Route path="/shared/brand-kit/:shareToken" element={<SharedBrandKitPage />} />
      <Route path="/proposal/:shareToken" element={<PublicProposalPage />} />
      <Route path="/p/:shareToken" element={<PublicPitchPage />} />
      <Route path="/preset/:shareToken" element={<PublicSharedPresetPage />} />
      <Route path="/r/:remoteToken" element={<PitchRemotePage />} />
      <Route path="/seller/:sellerId" element={<SellerProfilePage />} />
      <Route path="/studio/:slug" element={<SellerProfilePage />} />
      <Route path="/proposal/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/packages/gallery" element={<PublicPackagesGalleryPage />} />
      <Route path="/packages/p/:shareToken" element={<PackageMicrositePage />} />
      <Route path="/book/:slug" element={<PublicBookingPage />} />
      <Route path="/templates" element={<TemplateGalleryPage />} />
      <Route path="/welcome" element={<LandingPage />} />
      <Route path="/for/:slug" element={<ForSlugRouter />} />{/* Sprint 1089-LP v4 Phase C — persona + industry SEO */}
      <Route path="/sandbox/:industry" element={<SandboxPage />} />
      <Route path="/coming-soon" element={<ComingSoonPage variant="creative" />} />
      <Route path="/business" element={<ComingSoonPage variant="business" />} />
      <Route path="/__gate-status" element={<GateStatusPage />} />
      {/* Compass · Phase C · Trust Center — public, magic-link gated */}
      <Route path="/trust" element={<TrustCenter />} />
      <Route path="/trust/access" element={<TrustCenter />} />
      <Route path="/trust/verify" element={<TrustBundleVerify />} />
      <Route path="/waitlist/verified" element={<WaitlistVerifiedPage />} />
      <Route path="/launch/champions" element={<LaunchChampionsPage />} />
      <Route path="/trust" element={<TrustPage />} />
      <Route path="/security" element={<TrustPage />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/system-status" element={<StatusPage />} />
      <Route path="/become-ambassador" element={<AmbassadorPublicPage />} />
      <Route path="/join-agency/:token" element={<JoinAgencyPage />} />
      <Route path="/flows" element={<FlowsPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/compare/:competitor" element={<ComparePage />} />
      <Route path="/vs/:competitor" element={<ComparePage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPage />} />
      {/* Sprint 1070.D1.6 — BLG-001 public reader for user blog posts. */}
      <Route path="/post/:slug" element={<PublicBlogPostPage />} />
      {/* Sprint 1070.D1.8 — BLG-004 site-scoped routes so a tenant's
          /sites/{projectId}/blog/{slug} hits the connected reader. */}
      <Route path="/sites/:projectId/blog" element={<PublicBlogIndexPage />} />
      <Route path="/sites/:projectId/blog/:slug" element={<PublicBlogPostPage />} />
      <Route path="/integrations" element={<IntegrationsPage />} />
      <Route path="/resources" element={<ResourcesPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/for-students" element={<ForStudentsPage />} />
      <Route path="/for-schools" element={<ForSchoolsPage />} />
      <Route path="/awards" element={<IndustryAwardsPage />} />
      <Route path="/clinic" element={<ClinicPublicPage />} />
      <Route path="/educators/:slug" element={<EducatorPublicPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-of-service" element={<PublicTermsPage />} />
      {/* Sprint 946 — Launch Hub public surfaces */}
      <Route path="/leaderboard" element={<PublicLeaderboardPage />} />
      <Route path="/changelog" element={<PublicChangelogPage />} />
      <Route path="/press" element={<PublicPressKitPage />} />
      <Route path="/imprint" element={<ImprintPage />} />
      <Route path="/gdpr" element={<GDPRPage />} />
      <Route path="/sso-complete" element={<SSOCompletePage />} />
      <Route path="/" element={<LandingPage />} />
      {/* Sprint 971 — Public headless Big Screen endpoint (requires ?token=) */}
      <Route path="/platform/launch/big-screen/embed" element={<LaunchBigScreenPage />} />
      <Route path="/*" element={
        <ProtectedRoute>
          <OnboardingGate>
            <Routes>
              <Route path="/onboarding" element={<OnboardingWizardPage />} />
              <Route path="/proposal-builder/:proposalId/pitch/present" element={<PitchPresentPage />} />
              {/* Sprint 969 — Launch Big Screen renders fullscreen (no app chrome) */}
              <Route path="/platform/launch/big-screen" element={<LaunchBigScreenPage />} />
              <Route path="/*" element={
                <Layout>
                  <Routes>
                    <Route path="/dashboard" element={<MissionControlPage />} />
              <Route path="/mission-control" element={<Navigate to="/dashboard" replace />} />
              <Route path="/leads" element={<LeadsPage />} />
              <Route path="/leads/new" element={<CreateLeadPage />} />
              <Route path="/leads/:id" element={<LeadDetailPage />} />
              <Route path="/kanban" element={<KanbanPage />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/meetings" element={<MeetingsPage />} />
              <Route path="/calendar/subscriptions" element={<CalendarSubscriptionsPage />} />
              <Route path="/email-ingest" element={<EmailIngestPage />} />
              <Route path="/email-marketplace" element={<Navigate to="/marketplace?type=email" replace />} />
              {/* Sprint 982 — fix blank page for stale /platform/email-marketplace bookmarks */}
              <Route path="/platform/email-marketplace" element={<Navigate to="/marketplace?type=email" replace />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/ai-reports" element={<ReportingPage />} />
              <Route path="/analytics" element={<AnalyticsPage />} />
              <Route path="/quotes" element={<QuotesPage />} />
              <Route path="/quotes/compare" element={<QuoteComparePage />} />
              <Route path="/quotes/builder/new" element={<QuoteBuilderPage />} />
              <Route path="/quotes/builder/:quoteId" element={<QuoteBuilderPage />} />
              <Route path="/quotes/proposal/:quoteId" element={<ProposalComposerPage />} />
              <Route path="/proposal-builder" element={<ProposalBuilderListPage />} />
              <Route path="/imports/drafts" element={<ImportDraftsListPage />} />
              <Route path="/imports/drafts/:draftId" element={<ImportDraftDetailPage />} />
              <Route path="/proposal-builder/:proposalId" element={<ProposalBuilderEditorPage />} />
              <Route path="/proposal-builder/:proposalId/pitch" element={<PitchEditorPage />} />
              <Route path="/proposal-builder/:proposalId/pitch/presenter-view" element={<PresenterViewPage />} />
              <Route path="/pitch-theme-builder" element={<PitchThemeBuilderPage />} />
              <Route path="/packages" element={<PackagesPage />} />
              <Route path="/one-page-pitches" element={<OnePagePitchesPage />} />
              <Route path="/proposal-analytics" element={<UnifiedAnalyticsPage />} />
              <Route path="/quote-analytics" element={<UnifiedAnalyticsPage />} />
              <Route path="/quotes/new" element={<QuoteDetailPage />} />
              <Route path="/catalog" element={<CatalogPage />} />
              <Route path="/catalog/services" element={<CatalogPage />} />
              <Route path="/catalog/bundles" element={<BundlesPage />} />
              <Route path="/catalog/duplicates" element={<DuplicatesPage />} />
              <Route path="/catalog/health" element={<CatalogHealthPage />} />
              <Route path="/catalog/maintenance-calendar" element={<MaintenanceCalendarPage />} />
              <Route path="/catalog/gear-calendar" element={<GearCalendarPage />} />
              <Route path="/crew" element={<CrewDirectoryPage />} />
              <Route path="/crew/skill-matrix" element={<SkillMatrixPage />} />
              <Route path="/crew/:crewId" element={<CrewMemberPage />} />
              <Route path="/catalog/:itemId" element={<CatalogItemPage />} />
              <Route path="/catalog/kits" element={<KitsPage />} />
              <Route path="/quotes/branding" element={<BrandingPage />} />
              <Route path="/catalog/docs" element={<Navigate to="/company-branding?tab=documents" replace />} />
              <Route path="/catalog/docs/design" element={<Navigate to="/company-branding?tab=documents" replace />} />
              <Route path="/document-editor" element={<Navigate to="/company-branding?tab=documents" replace />} />
              <Route path="/document-editor/:templateId" element={<Navigate to="/company-branding?tab=documents" replace />} />
              <Route path="/quotes/:quoteId" element={<QuoteDetailPage />} />
              <Route path="/briefs" element={<BriefsPage />} />
              <Route path="/briefs/:briefId" element={<BriefEditorPage />} />
              <Route path="/contacts" element={<ClientsPage />} />
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/invoices" element={<InvoicesPage />} />
              <Route path="/projects" element={<ProjectsPage />} />
              <Route path="/projects/gantt" element={<GanttPage />} />
              <Route path="/kickoffs" element={<KickoffsPage />} />
              <Route path="/kickoffs/:kickoffId" element={<KickoffsPage />} />
              <Route path="/crew-resources" element={<CrewResourcesPage />} />
              <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
              <Route path="/calendar/production" element={<ProductionCalendarPage />} />
              <Route path="/my-tasks" element={<MyTasksPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/company-branding" element={<CompanyBrandingPage transitionalBanner />} />
              {/* Sprint Brand-Studio-Surface-Restore (2026-06-05) — Phase 7
                  split per BRAND_STUDIO_MIGRATION_AUDIT.md row 11.
                  Three single-purpose homes under System share the
                  CompanyBrandingPage component via forcedTab + hideTabs. */}
              <Route path="/system/company-profile" element={<CompanyBrandingPage forcedTab="contact" hideTabs />} />
              <Route path="/system/legal/terms" element={<CompanyBrandingPage forcedTab="terms" hideTabs />} />
              <Route path="/system/legal/documents" element={<CompanyBrandingPage forcedTab="documents" hideTabs />} />
              <Route path="/opportunities" element={<Navigate to="/kanban" replace />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/access-center" element={<AccessCenterPage />} />
              <Route path="/administration" element={<AdministrationPage />} />
              <Route path="/administration/calendar" element={<AdministrationPage />} />
              <Route path="/administration/health" element={<ClientHealthPage />} />
              <Route path="/budgeting" element={<Navigate to="/financials" replace />} />
              <Route path="/financials" element={<FinancialsPage />} />
              <Route path="/marketing" element={<MailingSystemPage />} />
              <Route path="/marketing/dashboard" element={<MarketingDashboardPage />} />
              <Route path="/marketing/website" element={<WebsiteBuilderPage />} />
              <Route path="/marketing/website/editor/:projectId" element={<WebsiteEditorPage />} />
              {/* Sprint 1060a — Web Studio unifying shell */}
              <Route path="/web-studio" element={<WebStudioPage />} />
              <Route path="/sales-studio" element={<SalesStudioPage />} />
              <Route path="/marketing-studio" element={<MarketingStudioPage />} />
              <Route path="/projects-studio" element={<ProjectsStudioPage />} />
              <Route path="/catalog-studio" element={<CatalogStudioPage />} />
              {/* Sprint 1070.A0 — Industry-adaptive Finance + Operate Studios */}
              <Route path="/finance-studio" element={<FinanceStudioPage />} />
              <Route path="/operate-studio" element={<OperateStudioPage />} />
              {/* Sprint 1065 — Concept Room */}
              <Route path="/concept-room" element={<ConceptRoomListPage />} />
              <Route path="/concept-room/:sessionId" element={<ConceptRoomSessionPage />} />
              <Route path="/checkout/success" element={<CheckoutResultPage />} />
              <Route path="/checkout/cancel" element={<CheckoutResultPage />} />
              <Route path="/marketing/copywriter" element={<Navigate to="/creative-studio?section=writers-room" replace />} />
              <Route path="/marketing/social" element={<LegacySocialHubRedirect />} />
              <Route path="/marketing/social-hub" element={<LegacySocialHubRedirect />} />
              <Route path="/social-content-hub" element={<LegacySocialHubRedirect />} />
              <Route path="/creative-hub" element={<Navigate to="/creative-studio?section=make" replace />} />
              <Route path="/marketing/creative-hub" element={<Navigate to="/creative-studio?section=make" replace />} />
              {/* Sprint 985 — Creative Studio LIVE (founder-approved Sprint 984). Real shell wraps the existing components. */}
              <Route path="/creative-studio" element={<CreativeStudioPage />} />
              <Route path="/creative-studio/preview" element={<CreativeStudioMockPage />} />
              <Route path="/creative-studio/diff" element={<CreativeStudioDiffPage />} />
              <Route path="/marketing/research" element={<MarketResearcherPage />} />
              <Route path="/marketing/brand-setup" element={<Navigate to="/brand-studio/foundation/setup" replace />} />
              <Route path="/marketing/brand-launch" element={<BrandLaunchPage />} />
              <Route path="/marketing/diagnostic" element={<Navigate to="/brand-studio/strengthen/diagnostic" replace />} />
              <Route path="/marketing/positioning" element={<Navigate to="/brand-studio/strengthen/positioning" replace />} />
              <Route path="/marketing/plan" element={<PlanAgentPage />} />
              <Route path="/marketing/social-batch" element={<SocialBatchAgentPage />} />
              <Route path="/marketing/email-sequence" element={<EmailSequenceAgentPage />} />
              <Route path="/marketing/landing" element={<LandingPageAgentPage />} />
              <Route path="/marketing/brand-dashboard" element={<Navigate to="/brand-studio/brand-health" replace />} />
              <Route path="/marketing/plans" element={<MarketingPlanPage />} />
              <Route path="/marketing/roi" element={<MarketingROIPage />} />
              <Route path="/marketing-roi" element={<Navigate to="/marketing/roi" replace />} />
              <Route path="/marketing/brand-repository" element={<Navigate to="/brand-studio/foundation/repository" replace />} />
              <Route path="/marketing/seo-analyzer" element={<SEOAnalyzerPage />} />
              <Route path="/marketing/content-calendar" element={<Navigate to="/creative-studio?section=social&tab=calendar" replace />} />
              <Route path="/marketing/competitor-intel" element={<Navigate to="/brand-studio/strengthen/competitor" replace />} />
              <Route path="/marketing/ai-positioning" element={<Navigate to="/brand-studio/strengthen/positioning" replace />} />
              {/* ──────────────────────────────────────────────────────────
                  Sprint Brand-Studio-Surface-Restore (2026-06-05) —
                  17 /brand-studio/* routes per the Canonical Recovery
                  Blueprint (/app/memory/SYSTEM_AUDIT/brand-studio-canonical-architecture.md)
                  + BrandStudioPage SECTION_REGISTRY + missionRegistry.
                  These were defined but never wired — the missing routes
                  were the root of the Brand Studio regression.
                  ────────────────────────────────────────────────────── */}
              <Route path="/brand-studio" element={<BrandStudioPage />} />
              <Route path="/brand-studio/home" element={<BrandStudioHomePage />} />
              {/* Foundation pillar (5) */}
              <Route path="/brand-studio/foundation/setup" element={<BrandSetupPage />} />
              {/* Sprint Brand-Studio-Surface-Restore (2026-06-05 PM-5) — the
                  `/brand-studio/foundation/identity` route is the IDENTITY SURFACE
                  (logos, brand assets, company identity, brand book), NOT the
                  Brand Identity ANALYSIS report (that's a different page wired
                  under /protect/identity-analysis). Per the screenshots paco
                  shared 2026-06-02, this is the CompanyBrandingPage identity
                  tab with the A/B/C section reorder from Sprint C2.2. */}
              <Route path="/brand-studio/foundation/identity" element={<CompanyBrandingPage forcedTab="identity" hideTabs />} />
              <Route path="/brand-studio/foundation/voice" element={<BrandVoicePage />} />
              <Route path="/brand-studio/foundation/repository" element={<BrandRepositoryPage />} />
              <Route path="/brand-studio/foundation/launch" element={<BrandLaunchPage />} />
              {/* Protect pillar (4) */}
              <Route path="/brand-studio/protect/identity-analysis" element={<BrandIdentityAnalysisPage />} />
              <Route path="/brand-studio/protect/domain-portfolio" element={<DomainPortfolioPage />} />
              <Route path="/brand-studio/protect/trademark-readiness" element={<BrandProtectionWorkspacePage />} />
              <Route path="/brand-studio/protect/evidence-center" element={<EvidenceCenterPage />} />
              {/* Strengthen pillar (3) */}
              <Route path="/brand-studio/strengthen/diagnostic" element={<BrandDiagnosticPage />} />
              <Route path="/brand-studio/strengthen/positioning" element={<AIPositioningPage />} />
              <Route path="/brand-studio/strengthen/competitor" element={<CompetitorIntelPage />} />
              {/* Health & Performance pillar (2) */}
              <Route path="/brand-studio/brand-health" element={<BrandDashboardPage />} />
              <Route path="/brand-studio/brand-performance" element={<BrandPerformancePage />} />
              {/* Steward pillar (1) */}
              <Route path="/brand-studio/steward/living-bi" element={<LivingBIPage />} />
              <Route path="/contracts" element={<ContractsPage />} />
              <Route path="/intake-forms" element={<IntakeFormsPage />} />
              {/* Forms Engine · ADR-002 — neutral alias. /forms = /intake-forms. */}
              <Route path="/forms" element={<IntakeFormsPage />} />
              <Route path="/chat/conversations" element={<ChatConversationsPage />} />
              <Route path="/rate-cards" element={<RateCardsPage />} />
              <Route path="/rate-intelligence" element={<RateIntelligencePage />} />
              <Route path="/project-calendar" element={<ProjectCalendarPage />} />
              <Route path="/profitability" element={<ProfitabilityPage />} />
              <Route path="/reporting" element={<ReportingPage />} />
              <Route path="/super-admin" element={<SuperAdminPage />} />
              <Route path="/platform" element={<ControlRoomPage />} />
              {/* Sprint Control-Room-Alias 2026-06-04 — convenience alias
                  so deep links from external docs / Sentry breadcrumbs
                  resolve correctly. */}
              <Route path="/platform/control-room" element={<ControlRoomPage />} />
              <Route path="/platform/control-room/governance/live-residual-dashboard" element={<LiveResidualDashboardPage />} />
              {/* Sprint 946H — Dev Library (markdown knowledge base) */}
              <Route path="/platform/dev-library" element={<DevLibraryPage />} />
              <Route path="/platform/master-index" element={<MasterIndexPage />} />
              <Route path="/platform/capability-index" element={<PCIPage />} />
              {/* Compass · 2026-05-21 — R2 Object Browser (super-admin · paco-exclusive) */}
              <Route path="/platform/r2-browser" element={<R2BrowserPage />} />
              <Route path="/platform/education" element={<EducationControlRoomPage />} />
              {/* Sprint 973A — Blog admin */}
              <Route path="/platform/blog" element={<BlogAdminPage />} />
              {/* Sprint 973B — External Website Integration Path A */}
              <Route path="/platform/external-site" element={<WebStudioShellWrap activeSection="integrate" activeItemKey="integrate-hub"><ExternalSiteHubPage /></WebStudioShellWrap>} />
              <Route path="/platform/external-site/integrate" element={<WebStudioShellWrap activeSection="integrate" activeItemKey="integrate-connect"><ExternalSiteIntegratePage /></WebStudioShellWrap>} />
              <Route path="/platform/external-site/funnel" element={<WebStudioShellWrap activeSection="integrate" activeItemKey="integrate-funnel"><ConversionFunnelPage /></WebStudioShellWrap>} />
              {/* Sprint 974D-Phase 1 — Migration Wizard Path B */}
              <Route path="/platform/external-site/migrate" element={<WebStudioShellWrap activeSection="create" activeItemKey="create-import"><MigrationWizardPage /></WebStudioShellWrap>} />
              {/* Sprint 974A — Catalog widgets (Path A enhancement) */}
              <Route path="/platform/external-site/catalog-widgets" element={<WebStudioShellWrap activeSection="widgets" activeItemKey="widgets-catalog"><CatalogWidgetsPage /></WebStudioShellWrap>} />
              {/* Sprint 974B — Newsletter widgets (Path A enhancement) */}
              <Route path="/platform/external-site/newsletter-widgets" element={<WebStudioShellWrap activeSection="widgets" activeItemKey="widgets-newsletter"><NewsletterWidgetsPage /></WebStudioShellWrap>} />
              {/* Sprint 974C — Shop widgets (Path A · Stripe-powered) */}
              <Route path="/platform/external-site/shop-widgets" element={<WebStudioShellWrap activeSection="widgets" activeItemKey="widgets-shop"><ShopWidgetsPage /></WebStudioShellWrap>} />
              {/* Sprint 974E — Public REST API key management */}
              <Route path="/platform/api-keys" element={<ApiKeysPage />} />
              <Route path="/platform/payouts" element={<PayoutsPage />} />
              <Route path="/platform/marketplace-revenue" element={<MarketplaceRevenuePage />} />
              {/* Sprint 982 — Pre-Launch Readiness dashboard */}
              <Route path="/platform/readiness" element={<ReadinessPage />} />
              {/* Sprint 982 fix — Zapier panel moved to /platform/integrations/zapier so the IntegrationsRegistryPage (Custom Keys vault, GSC, Twilio, Stripe Webhook Secret cards) can own /platform/integrations */}
              <Route path="/platform/integrations/zapier" element={<PlatformIntegrationsPage />} />
              <Route path="/platform/finance/classroom-invoicing" element={<ClassroomInvoicingPage />} />
              {/* Sprint 1070.A9 — 1099 tax-season seller earnings export (super-admin) */}
              <Route path="/platform/finance/1099" element={<Admin1099Page />} />
              {/* Sprint 1070.B3 — Quarter Recap (super-admin, on-demand LLM verdict) */}
              <Route path="/platform/quarter-recap" element={<QuarterRecapPage />} />
              {/* Sprint A.1 — Brand Asset Activity Timeline (super-admin · ADR-018 §A.5) */}
              <Route path="/platform/brand-asset-activity" element={<BrandAssetActivityPage />} />
              {/* Forms Engine · Wave 2 Track 3 (ADR-016) — admin surface */}
              <Route path="/platform/engines/forms" element={<FormsEnginePage />} />
              {/* Compass G.2 (2026-05-26) — Side-by-side snapshot diff viewer */}
              <Route path="/platform/compass/snapshots/diff" element={<CompassSnapshotDiffPage />} />
              {/* Compass S.2 (2026-05-26) — Translation triage console */}
              <Route path="/platform/compass/translations" element={<CompassTranslationsPage />} />
              {/* Compass G.4 (2026-05-27) — Grounding Playground */}
              <Route path="/platform/compass/grounding-playground" element={<CompassGroundingPlaygroundPage />} />
              {/* Compass Hub Mockup (2026-05-29) — Visual preview of unified 3-tab shell */}
              <Route path="/platform/compass" element={<CompassHubPage />} />
              <Route path="/platform/compass/preview" element={<CompassHubPage />} />
              {/* Mission Video Engine V3.1 — Control Room → Operations · paco-only HITL video pipeline */}
              <Route path="/platform/mission-video-studio" element={<MissionVideoStudioPage />} />
              <Route path="/platform/links-registry" element={<LinksRegistryPage />} />
              {/* Consolidated System Health (merges System Audit + System Flows) */}
              <Route path="/platform/system-health" element={<SystemAuditPage />} />              {/* Legacy — redirect to consolidated System Health */}
              <Route path="/platform/system-audit" element={<Navigate to="/platform/system-health?tab=audit" replace />} />
              <Route path="/platform/system-flows" element={<Navigate to="/platform/system-health?tab=flows" replace />} />
              {/* Legacy — Component Map folded into System Intelligence */}
              <Route path="/platform/component-map" element={<Navigate to="/platform/system-intelligence?tab=components" replace />} />
              <Route path="/platform/email-logs" element={<EmailLogsPage />} />
              <Route path="/platform/localization" element={<LocalizationHubPage />} />
              {/* Legacy — Onboarding Analytics folded into Ops Dashboard */}
              <Route path="/platform/onboarding-analytics" element={<Navigate to="/platform/ops-dashboard?tab=onboarding" replace />} />
              <Route path="/platform/system-intelligence" element={<SystemIntelligencePage />} />
              {/* Legacy — Financial Cockpit + Template Funnel folded into Monetization Hub */}
              <Route path="/platform/financial-cockpit" element={<Navigate to="/platform/monetization?tab=financials" replace />} />
              <Route path="/platform/template-funnel" element={<Navigate to="/platform/monetization?tab=funnel" replace />} />
              <Route path="/platform/monetization" element={<MonetizationHubPage />} />
              {/* Sprint 937 — Service Monitor retired (data merged into Ops Dashboard) */}
              <Route path="/platform/service-monitor" element={<Navigate to="/platform/ops-dashboard?tab=services" replace />} />
              {/* Sprint 937 — Service Tracking is now a tab inside Ops Dashboard */}
              <Route path="/platform/integrations-registry" element={<Navigate to="/platform/ops-dashboard?tab=services" replace />} />
              <Route path="/platform/integrations" element={<IntegrationsRegistryPage />} />
              <Route path="/platform/waitlist-diag" element={<WaitlistDiagPage />} />
              <Route path="/platform/launch-broadcast" element={<LaunchBroadcastPage />} />
              <Route path="/platform/nurture-drip" element={<NurtureDripPage />} />
              {/* Sprint 946 — Launch Hub */}
              <Route path="/platform/launch" element={<LaunchHubPage />} />
              <Route path="/platform/launch/growth" element={<GrowthDashboardPage />} />
              <Route path="/platform/launch/big-screen" element={<LaunchBigScreenPage />} />
              {/* Sprint 971 — Service tokens admin (headless display tokens) */}
              <Route path="/platform/admin/service-tokens" element={<AdminServiceTokensPage />} />
              <Route path="/platform/launch/qr-codes" element={<QRGeneratorPage />} />
              <Route path="/platform/launch/press-kit" element={<PressKitPage />} />
              <Route path="/platform/launch/changelog" element={<ChangelogEditorPage />} />
              <Route path="/platform/launch/leaderboard" element={<ReferralLeaderboardPage />} />
              <Route path="/platform/launch/config" element={<LaunchConfigPage />} />
              <Route path="/platform/email-domain" element={<EmailDomainPage />} />
              <Route path="/platform/admin-index" element={<AdminIndexPage />} />
              <Route path="/platform/translations" element={<LocalizationHubPage />} />
              <Route path="/platform/i18n-coverage" element={<LocalizationHubPage />} />
              <Route path="/platform/ops-dashboard" element={<OpsDashboardPage />} />
              <Route path="/platform/permissions" element={<PermissionsPage />} />
              {/* Sprint 913 — unified AI & Credits hub */}
              <Route path="/platform/ai-credits" element={<AICreditsHubPage />} />
              {/* Legacy aliases — preserve old bookmarks */}
              <Route path="/platform/ai-usage" element={<Navigate to="/platform/ai-credits" replace />} />
              <Route path="/platform/ai-spend" element={<Navigate to="/platform/ai-credits" replace />} />
              {/* Sprint 913 — Security Posture Hub (drilled from Control Room card) */}
              <Route path="/platform/security" element={<SecurityPostureHubPage />} />
              <Route path="/platform/storage" element={<StorageHubPage />} />
              <Route path="/platform/support-tickets" element={<SupportTicketsPage />} />
              <Route path="/industry-templates" element={<IndustryTemplateGalleryPage />} />
              <Route path="/template-marketplace" element={<Navigate to="/marketplace?type=proposal" replace />} />
              <Route path="/marketplace" element={<MarketplaceHubPage />} />
              <Route path="/marketplace/purchase-success" element={<MarketplacePurchaseSuccessPage />} />
              <Route path="/marketplace/listing/:listingId" element={<MarketplaceListingDetailPage />} />
              <Route path="/marketplace/:listingId" element={<MarketplaceHubPage />} />
              <Route path="/seller-dashboard" element={<SellerDashboardPage />} />
              <Route path="/seller-payouts" element={<SellerPayoutsPage />} />
              <Route path="/marketplace-admin" element={<MarketplaceAdminPage />} />
              <Route path="/help" element={<KnowledgeBasePage />} />
              <Route path="/ambassador" element={<AmbassadorPage />} />
              <Route path="/agency-dashboard" element={<AgencyDashboardPage />} />
              <Route path="/journeys" element={<JourneyMapPage />} />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Layout>
          } />
            </Routes>
          </OnboardingGate>
        </ProtectedRoute>
      } />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
    <ThemeProvider>
      <LanguageProvider>
        <BrowserRouter>
          <BuilderCommandPaletteProvider>
            <CurrencyIdentityProvider>
              <AppRouter />
              <ThemedToaster />
              <CreditDepletionModal />
            </CurrencyIdentityProvider>
          </BuilderCommandPaletteProvider>
        </BrowserRouter>
      </LanguageProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

function ThemedToaster() {
  const { themeName } = useTheme();
  return (
    <Toaster 
      position="top-center" 
      toastOptions={{ 
        style: themeName === "dark" 
          ? { background: "#27272A", color: "#FAFAFA", borderRadius: "12px", border: "1px solid #3F3F46" }
          : { background: "#FFFFFF", color: "#18181B", borderRadius: "12px", border: "1px solid #E4E4E7" }
      }} 
    />
  );
}

export default App;
